import React, { useEffect, useState } from 'react'
import Table from "react-bootstrap/Table";
import { Modal } from 'react-bootstrap';
import CustomPagination from '../components/CustomPagination';
import DeleteModal from '../components/DeleteModal';
import Loader from '../components/Loader';
import { deleteReportedBoardItem, editBoardItem, getReportedBoardItems, getReportedPendingCount, refreshReportedCount } from '../controller';
import { requestHandler } from '../helper';
import { formatDateAsItIs } from '../helper/common';
import { toast } from 'react-toastify';

const fullName = (u) => [u?.firstName, u?.lastName].filter(Boolean).join(' ') || u?.userName || '-';
const short = (text, max = 80) => (text && text.length > max ? `${text.slice(0, max - 1)}…` : text || '-');

// "Flagged Board Posts" tab of Reported Content (AO 2.0 D4). Same table design as Flagged Stories: one row per report.
function ReportedBoard({ limit = 10 }) {
  const [type, setType] = useState('thread');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const [confirm, setConfirm] = useState(null); // { action: 'approve' | 'reject' | 'delete', id }
  const [paginationValue, setPaginationValue] = useState({ page: 1, totalData: 0, totalPages: 0 });
  const { page, totalData, totalPages } = paginationValue;
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, totalData);
  const isReply = type === 'reply';

  // pending reports per sub-tab (badge beside Threads / Replies); recounted after every admin action
  const [pendingCounts, setPendingCounts] = useState({});
  useEffect(() => {
    const load = () => requestHandler(
      async () => await getReportedPendingCount(),
      null,
      (res) => setPendingCounts(res.data || {}),
      () => { }
    );
    load();
    window.addEventListener('reported-count-refresh', load);
    return () => window.removeEventListener('reported-count-refresh', load);
  }, []);
  const badge = (key) => pendingCounts[key] > 0 ? <span className="tab-badge ms-2">{pendingCounts[key] > 99 ? '99+' : pendingCounts[key]}</span> : null;

  const getRows = async (pageNo = page) => {
    await requestHandler(
      async () => await getReportedBoardItems(type, limit, pageNo),
      setLoading,
      (res) => {
        const { data, page, totalData, totalPages } = res.data;
        setRows(data);
        setPaginationValue({ page, totalData, totalPages });
      }
    );
  };

  useEffect(() => {
    getRows(page);
    // eslint-disable-next-line
  }, [page, limit, type]);

  const handleConfirm = async () => {
    const { action, id } = confirm;
    await requestHandler(
      async () => await (action === 'delete' ? deleteReportedBoardItem(type, id) : editBoardItem({ status: action === 'approve' ? 1 : 2 }, type, id)),
      setLoading,
      (res) => {
        toast.success(res.message);
        refreshReportedCount();
        setConfirm(null);
        // step back a page when the last item on it was handled
        if (action === 'delete' && rows.length === 1 && page > 1) {
          setPaginationValue((prev) => ({ ...prev, page: page - 1 }));
        } else {
          getRows(page);
        }
      }
    );
  };

  const askConfirm = (action, id) => setConfirm({ action, id });

  const switchType = (next) => {
    if (next === type) return;
    setRows([]);
    setType(next);
    setPaginationValue({ page: 1, totalData: 0, totalPages: 0 });
  };

  return (
    <>
      {loading && <Loader />}
      <div className="d-flex gap-2 mb-3">
        <button className={`btn ${!isReply ? 'btn-primary' : 'btn-outline-light'}`} onClick={() => switchType('thread')}>Threads{badge('boardThread')}</button>
        <button className={`btn ${isReply ? 'btn-primary' : 'btn-outline-light'}`} onClick={() => switchType('reply')}>Replies{badge('boardReply')}</button>
      </div>
      <div className="overflow-x-auto theme-scrollbar">
        <Table className="theme-table">
          <thead>
            <tr>
              <th>Post By</th>
              <th>Crew</th>
              <th>{isReply ? 'Reply' : 'Thread'}</th>
              <th>Post Date</th>
              <th>Reported By</th>
              <th>Flagged Reason</th>
              <th>Reported Date</th>
              <th>Status</th>
              <th width="200" className='text-center'>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.length > 0 ? (
              rows.flatMap((row) => (row.reportId || []).map((report, index) => (
                <tr key={`${row._id}-${index}`}>
                  <td>{fullName(row.authorId)}</td>
                  <td>{row.crewId?.name || '-'}</td>
                  <td style={{ cursor: 'pointer' }} onClick={() => setViewing(row)}>
                    {isReply ? short(row.body) : short(row.title)}
                  </td>
                  <td>{formatDateAsItIs(row.createdAt)}</td>
                  <td>{fullName(report?.senderId)}</td>
                  <td>{report?.reason || "-"}</td>
                  <td>{formatDateAsItIs(report?.createdAt)}</td>
                  <td>
                    <ul className="table-status">
                      <li className={row.status === 1 ? 'status-success' : 'status-danger'}>
                        {row.status === 1 ? 'Approved' : row.status === 2 ? 'Rejected' : 'Pending'}
                      </li>
                    </ul>
                  </td>
                  <td width={220}>
                    <div className='d-flex gap-3 justify-content-center align-items-center'>
                      {!row.status && (
                        <>
                          <button className="btn btn-light-danger" onClick={() => askConfirm('reject', row._id)}>
                            <img src="/images/svg/table-false.svg" alt="icon" />
                          </button>
                          <button className="btn btn-light-success" onClick={() => askConfirm('approve', row._id)}>
                            <img src="/images/svg/table-true.svg" alt="icon" />
                          </button>
                        </>
                      )}
                      {row.status > 0 && (
                        <button className="btn btn-light-danger" onClick={() => askConfirm('delete', row._id)}>
                          <img src="/images/svg/table-delete.svg" alt="icon" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )))
            ) : (
              <tr>
                <td colSpan={9} className="text-center p-5 fs-5">
                  No Records Found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap mt-1">
        <p className="mb-0 fs-16 fw-500 text-light">
          Showing {end === 0 ? 0 : start} to {end} of {totalData} entries
        </p>
        {totalPages > 1 && (
          <CustomPagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={(p) => setPaginationValue({ ...paginationValue, page: p })}
          />
        )}
      </div>

      <Modal show={!!viewing} onHide={() => setViewing(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{isReply ? `Reply in "${viewing?.threadId?.title || ''}"` : viewing?.title}</Modal.Title>
        </Modal.Header>
        {viewing && (
          <Modal.Body style={{ whiteSpace: 'pre-wrap' }}>
            {viewing.body || '-'}
          </Modal.Body>
        )}
      </Modal>

      <DeleteModal
        show={!!confirm}
        handleClose={() => setConfirm(null)}
        handleDelete={handleConfirm}
        title={confirm?.action === 'delete' ? 'Confirm Delete' : 'Confirmation'}
        message={confirm?.action === 'delete'
          ? `Are you sure you want to remove this ${isReply ? 'reply' : 'thread'}? It will be removed from the Crew Board for everyone.`
          : `Are you sure you want to ${confirm?.action} this ${isReply ? 'reply' : 'thread'}?`}
      />
    </>
  )
}

export default ReportedBoard
